import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import useAuthStore from '../store/useAuthStore';

const VerifyOTPPage = () => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [resendLoading, setResendLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [pageLoading, setPageLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, verifyOTP, resendOTP, isLoading } = useAuthStore();

  // Get email from location state or user store
  const email = location.state?.email || user?.email || '';

  useEffect(() => {
    // Start countdown for resend OTP
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Redirect to register if no email is available
  useEffect(() => {
    if (!email) {
      toast.error('No email found. Please register first.');
      navigate('/register');
    } else {
      setPageLoading(false);
    }
  }, [email, navigate]);

  // Auto-focus next input field
  const handleOtpChange = (index, value) => {
    
    if (value.length <= 1) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Auto-focus next input if value is entered
      if (value && index < 5) {
        const nextInput = document.querySelector(`input[data-index="${index + 1}"]`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  // Handle backspace to previous input
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.querySelector(`input[data-index="${index - 1}"]`);
      if (prevInput) prevInput.focus();
    }
  };

  // Handle paste OTP
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d{6}$/.test(pastedData)) {
      const newOtp = [...otp];
      for (let i = 0; i < 6; i++) {
        newOtp[i] = pastedData[i] || '';
      }
      setOtp(newOtp);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const otpString = otp.join('');
    
    if (otpString.length !== 6) {
      toast.error('Please enter the complete 6-digit OTP');
      return;
    }

    if (!email) {
      toast.error('Email not found. Please try registering again.');
      navigate('/register');
      return;
    }

    try {
      // Call the verify OTP API through store
      await verifyOTP(email, otpString);
      toast.success('Email verified successfully! You can now login.');
      navigate('/login');
    } catch (error) {
      // Show error message in toast
      console.error('OTP verification error:', error);
      toast.error(error.message || 'OTP verification failed');
    }
  };

  const handleResendOTP = async () => {
    if (!email) {
      toast.error('Email not found. Please try registering again.');
      navigate('/register');
      return;
    }

    setResendLoading(true);
    try {
      await resendOTP(email);
      toast.success('OTP resent successfully!');
      setCountdown(60); // Start 60 second countdown
    } catch (error) {
      // Error is already handled by the store
      console.error('Resend OTP error:', error);
    } finally {
      setResendLoading(false);
    }
  };

  if (pageLoading) {
    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
          <div className="loading-spinner mx-auto mb-6 w-12 h-12"></div>
          <p className="text-white dark:text-white text-lg font-medium">Loading verification page...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md animate-fade-in">
        <div className="text-center">
        
          <h2 className="mt-8 text-center text-3xl font-extrabold  dark:text-white animate-slide-up">
            Verify Your Email
          </h2>
          <p className="mt-4 text-center text-base text-white dark:text-white leading-relaxed animate-slide-up animation-delay-100">
            We've sent a 6-digit verification code to{' '}
            <span className="font-semibold text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 px-2 py-1 rounded-md">
              {email}
            </span>
          </p>
        </div>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-md animate-slide-up animation-delay-200">
        <div className="card card-body shadow-xl border-0 backdrop-blur-sm bg-white/95 dark:bg-gray-800/95">
          <form onSubmit={handleSubmit} className="space-y-8">
            <div>
              <label className="block text-sm font-semibold text-white dark:text-white mb-4 text-center">
                Enter Verification Code
              </label>
              <div className="flex justify-center gap-3">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    type="text"
                    data-index={index}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    className="otp-input w-14 h-14 text-center text-xl font-bold border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 dark:bg-gray-700 dark:text-white transition-all duration-200 hover:border-primary-400 dark:hover:border-primary-400 focus:scale-105 focus:shadow-lg"
                    maxLength={1}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="one-time-code"
                  />
                ))}
              </div>
              <p className="mt-4 text-xs text-white dark:text-white text-center">
                Enter the 6-digit code sent to your email
              </p>
              <p className="mt-2 text-xs text-white dark:text-white text-center">
                Current OTP length: {otp.join('').length}/6
              </p>
            </div>

            <div className="space-y-4">
              <button
                type="submit"
                disabled={isLoading || otp.join('').length !== 6}
                className={`w-full h-12 px-6 py-3 font-semibold rounded-xl shadow-lg transition-all duration-200 flex justify-center items-center ${
                  isLoading || otp.join('').length !== 6
                  ? 'bg-gray-400 cursor-not-allowed shadow-none transform-none'
                  : 'bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]'
                }`}
              >
                {isLoading ? (
                  <>
                    <div className="loading-spinner white mr-3 w-5 h-5"></div>
                    Verifying...
                  </>
                ) : (
                  'Verify Email'
                )}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={resendLoading || countdown > 0}
                  className={`inline-flex items-center px-6 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${
                    resendLoading || countdown > 0
                      ? 'text-white dark:text-white cursor-not-allowed bg-gray-100 dark:bg-gray-800 shadow-none'
                      : 'text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 bg-primary-50 hover:bg-primary-100 dark:bg-primary-900/20 dark:hover:bg-primary-900/30 hover:shadow-md active:scale-95'
                  }`}
                >
                  {resendLoading ? (
                    <>
                      <div className="loading-spinner primary mr-2 w-4 h-4"></div>
                      Sending...
                    </>
                  ) : countdown > 0 ? (
                    <>
                      <svg className="w-4 h-4 mr-2 animate-pulse text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                      Resend OTP in {countdown}s
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Resend OTP
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex flex-col sm:flex-row justify-center items-center space-y-3 sm:space-y-0 sm:space-x-6">
                <Link
                  to="/login"
                  className="inline-flex items-center text-sm font-medium text-white dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Back to Login
                </Link>
                <Link
                  to="/register"
                  className="inline-flex items-center text-sm font-medium text-white dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                  Register with different email
                </Link>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VerifyOTPPage;
